import { Business } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  addToBlacklist,
  getBlacklist,
  removeFromBlacklist,
  updateBlacklist,
} from 'lib/api/blackList';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import EnhancedTable from 'lib/designSystem/Table';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import {
  hasAnyPermissions,
  rutChanger,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

const HEADERS = [
  { key: 'businessIdentifier', label: 'RUT' },
  { key: 'businessName', label: 'Nombre empresa' },
  { key: 'reason', label: 'Razón' },
];

type Props = {
  businessIdentifier: string;
  setBusinessIdentifier: (businessIdentifier: string) => void;
};

const Blacklist = ({ businessIdentifier, setBusinessIdentifier }: Props) => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [currentBusinessName, setCurrentBusinessName] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [reason, setReason] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editId, setEditId] = useState(0);
  const [newReason, setNewReason] = useState('');
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const { data: blacklist, refetch: refetchBlacklist } = getBlacklist(
    dispatch,
    businessIdentifier,
  );

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setBusinessName('');
    setIdentifier('');
    setReason('');
  };

  const handleSubmit = async () => {
    if (!identifier || !businessName || !reason)
      return alert('Completa todos los campos');
    if (!validaRut(identifier)) {
      alert('Rut inválido');
      return;
    }
    setLoading(true);
    try {
      await addToBlacklist(dispatch, identifier, {
        reason,
        businessName,
      });
      await refetchBlacklist();
      showAlert(dispatch, 'success', 'Empresa agregada a la blacklist');
    } catch (error) {
      errorHandler(
        dispatch,
        error,
        'Hubo un error al agregar la empresa a la blacklist',
      );
    } finally {
      setLoading(false);
      handleCloseDialog();
    }
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await removeFromBlacklist(dispatch, identifier, { reason: removeReason });
      await refetchBlacklist();
      showAlert(dispatch, 'success', 'Empresa eliminada de la blacklist');
    } catch (error) {
      showAlert(dispatch, 'error', 'Hubo un error');
    } finally {
      setLoading(false);
      setOpenRemoveDialog(false);
      setRemoveReason('');
    }
  };

  const handleEdit = async () => {
    setLoading(true);
    try {
      await updateBlacklist(dispatch, { id: editId, reason: newReason });
      await refetchBlacklist();
      showAlert(dispatch, 'success', 'Razón actualizada');
    } catch (error) {
      showAlert(dispatch, 'error', 'Hubo un error');
    } finally {
      setLoading(false);
      handleCloseEditDialog();
    }
  };

  const handleOpenEditDialog = (id: number) => {
    setOpenEditDialog(true);
    setEditId(id);
    const blackListItem = blacklist?.data.find((item) => item.id === id);
    setCurrentBusinessName(blackListItem?.businessName || '');
    setNewReason(blackListItem?.reason || '');
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setNewReason('');
  };

  const handleDelete = (id: number) => {
    const blackListItem = blacklist?.data.find((item) => item.id === id);
    setCurrentBusinessName(blackListItem?.businessName || '');
    setIdentifier(blackListItem?.businessIdentifier || '');
    setOpenRemoveDialog(true);
  };

  const actions = [
    {
      action: handleDelete,
      actionLabel: 'Eliminar',
      canShow: (_e: string | number) =>
        hasAnyPermissions(user, [
          'settings',
          'settings_risk',
          'settings_risk_blacklist_delete',
        ]),
    },
    {
      action: handleOpenEditDialog,
      actionLabel: 'Editar',
      canShow: (_e: string | number) =>
        hasAnyPermissions(user, [
          'settings',
          'settings_risk',
          'settings_risk_blacklist_edit',
        ]),
    },
  ];

  const filters: Filter[] = [
    {
      label: 'RUT Empresa',
      type: 'autocomplete',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
      key: 'businessIdentifier',
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Title
          title="Empresas"
          icon={<Business color="primary" />}
          color="primary"
        />
        <Stack direction="row" gap={2}>
          <CustomButton
            onClick={() => setOpenDialog(true)}
            color="primary"
            disabled={
              !hasAnyPermissions(user, [
                'settings',
                'settings_risk',
                'settings_risk_blacklist_add',
              ])
            }
          >
            Agregar
          </CustomButton>
          <GlobalSearcher
            filters={filters}
            triggerSearch={refetchBlacklist}
            dispatch={dispatch}
            disableSearch={loading}
          />
        </Stack>
      </Stack>
      <EnhancedTable
        dataHeaders={HEADERS}
        actions={actions}
        data={blacklist?.data || []}
        rowsPerPageDefault={10}
        exportToFile="xlsx"
      />
      <Dialog
        open={openDialog}
        onClose={() => handleCloseDialog()}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Agregar empresa</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Agrega una empresa a tu blacklist
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="businessName"
            label="Nombre de la empresa"
            type="text"
            fullWidth
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
          />
          <TextField
            autoFocus
            margin="dense"
            id="identifier"
            label="RUT de la empresa"
            type="text"
            fullWidth
            value={identifier}
            onChange={(e) => rutChanger(e.target.value, setIdentifier)}
          />
          <TextField
            autoFocus
            margin="dense"
            id="reason"
            label="Razón"
            type="text"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={() => setOpenDialog(false)} color="secondary">
            Cancelar
          </CustomButton>
          <CustomButton onClick={() => handleSubmit()} color="primary">
            Añadir
          </CustomButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openEditDialog}
        onClose={() => handleCloseEditDialog()}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
      >
        <DialogTitle id="form-dialog-title">
          Editar razón de {currentBusinessName}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="newReason"
            label="Razón"
            type="text"
            fullWidth
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            multiline
          />
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => handleCloseEditDialog()}
            color="secondary"
          >
            Cancelar
          </CustomButton>
          <CustomButton onClick={() => handleEdit()} color="primary">
            Guardar
          </CustomButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openRemoveDialog}
        onClose={() => setOpenRemoveDialog(false)}
        aria-labelledby="remove-dialog-title"
        maxWidth="sm"
      >
        <DialogTitle id="remove-dialog-title">BlackList</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Estás seguro de querer eliminar a {currentBusinessName} de la
            blacklist? De ser así, escribe la razón para eliminar.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="removeReason"
            label="Razón para eliminar"
            type="text"
            fullWidth
            value={removeReason}
            onChange={(e) => setRemoveReason(e.target.value)}
            multiline
          />
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setOpenRemoveDialog(false)}
            color="secondary"
            disabled={loading}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => handleConfirmDelete()}
            color="primary"
            disabled={loading || !removeReason}
          >
            Eliminar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Blacklist;
